import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import { updateWebinar } from "store/updateWebinarSlice";
import { resetUpdateState } from "store/updateWebinarSlice";

const UpdateWebinarTable = ({color}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { loading, success, error } = useSelector((state) => state.updateWebinar);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_time: "",
    duration_minutes: "",
    capacity: "",
    price: "",
    image: null,
  });

  const [preview, setPreview] = useState(null);

  // 🧠 Fetch webinar details and prefill form
  useEffect(() => {
    const fetchWebinarDetail = async () => {
      const token = localStorage.getItem("access");
      try {
        const res = await axios.get(`https://api-workshop.firststepoverseas.com/webinars/${id}/`, {
          headers: { Authorization: `JWT ${token}` },
        });
        const w = res.data;
        console.log("Fetched webinar:", w);

        // Prefill the form with previous data
        setFormData({
          title: w.title || "",
          description: w.description || "",
          start_time: w.start_time ? w.start_time.slice(0, 16) : "",
          duration_minutes: w.duration_minutes || "",
          capacity: w.capacity || "",
          price: w.price || "",
          image: null, // image file not fetched directly
        });

        // Set image preview from API
        setPreview(w.image || null);
      } catch (err) {
        console.error("Error fetching webinar:", err);
      }
    };
    fetchWebinarDetail();
  }, [id]);

  // 📝 Handle form input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));
      setPreview(URL.createObjectURL(file)); // show new image preview
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 💾 Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    // convert to FormData (important for image upload)
    // const data = new FormData();
    // Object.entries(formData).forEach(([key, value]) => {
    //   if (value !== null && value !== "") {
    //     data.append(key, value);
    //   }
    // });

    dispatch(updateWebinar({ id, data: formData }));
  };

  // ✅ Handle success and error
  useEffect(() => {
    if (success) {
      Swal.fire("Updated!", "Webinar updated successfully.", "success");
      dispatch(resetUpdateState());
      history.push("/admin/webinar");
    }
    if (error) {
      Swal.fire("Error!", error.detail || error.message || "Something went wrong.", "error");
    }
  }, [success, error, dispatch, history]);

  return (
    <div
        className={
          "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        {/* Header */}
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center justify-between">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3
                className={
                  "font-semibold text-lg " +
                  (color === "light" ? "text-blueGray-700" : "text-white")
                }
              >
                Webinar
              </h3>
            </div>
          </div>
        </div>

    <div className="container mx-auto p-6 px-6 py-6">
      <h2 className="text-2xl font-bold mb-6 text-blue-600">Update Webinar</h2>

      <form
        onSubmit={handleSubmit}
        className="bg-white px-6 py-3 rounded shadow-md max-w-2xl mx-auto"
      >
        {/* Title */}
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full border rounded p-2"
            required
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="w-full border rounded p-2"
            required
          />
        </div>

        {/* Start Time + Duration */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block mb-1 font-semibold">Start Time</label>
            <input
              type="datetime-local"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              className="w-full border rounded p-2"
              required
            />
          </div>
          <div>
            <label className="block mb-1 font-semibold">Duration (min)</label>
            <input
              type="number"
              name="duration_minutes"
              value={formData.duration_minutes}
              onChange={handleChange}
              className="w-full border rounded p-2"
              required
            />
          </div>
        </div>

        {/* Capacity + Price */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block mb-1 font-semibold">Capacity</label>
            <input
              type="number"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              className="w-full border rounded p-2"
              required
            />
          </div>
          <div>
            <label className="block mb-1 font-semibold">Price</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              className="w-full border rounded p-2"
              required
            />
          </div>
        </div>

        {/* Image Upload */}
        <div className="mb-6">
          <label className="block mb-1 font-semibold">Image (optional)</label>
          {preview && (
            <img
              src={preview}
              alt="Preview"
              className="w-32 h-32 object-cover mb-2 rounded border"
            />
          )}
          <input
            type="file"
            name="image"
            onChange={handleChange}
            className="w-full"
            accept="image/*"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="bg-lightBlue-600 text-white px-6 py-2 rounded hover:bg-blue disabled:opacity-50"
        >
          {loading ? "Updating..." : "Update Webinar"}
        </button>
      </form>
    </div>
    </div>
  );
};

UpdateWebinarTable.defaultProps = {
  color: "light",
};

UpdateWebinarTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
};

export default UpdateWebinarTable;
